import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Package, BarChart3, Zap } from 'lucide-react';
import { loginUser, clearError } from '../redux/slices/authSlice';
import LoginForm, { DevoardLogo } from '../components/auth/LoginForm';
import AnimatedBackground from '../components/auth/AnimatedBackground';

const easeOut = { duration: 0.7, ease: [0.22, 1, 0.36, 1] };

const FEATURES = [
  { icon: Package, label: 'Inventory Management' },
  { icon: Zap, label: 'Fast Checkout' },
  { icon: BarChart3, label: 'Smart Reports' },
];

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, loading, error } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleLogin = async (credentials) => {
    const result = await dispatch(loginUser(credentials));
    if (loginUser.fulfilled.match(result)) {
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-teal-50">
      <AnimatedBackground />

      <div className="relative z-10 grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
        {/* ---- Branding side (large screens) ---- */}
        <div className="hidden items-center justify-center px-12 py-16 lg:flex">
          <div className="max-w-lg text-center">
            <motion.div
              initial={{ opacity: 0, y: -28, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={easeOut}
              className="flex justify-center"
            >
              <DevoardLogo className="max-h-24 w-auto max-w-[260px]" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...easeOut, delay: 0.22 }}
              className="mt-10 font-display text-4xl font-bold leading-tight text-gray-900"
            >
              The complete POS for
              <span className="bg-gradient-to-r from-primary-600 to-indigo-600 bg-clip-text text-transparent">
                {' '}
                modern retail
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...easeOut, delay: 0.34 }}
              className="mx-auto mt-5 max-w-md text-lg leading-relaxed text-gray-600"
            >
              Sales, inventory, reports and customer management — everything your
              business needs in one calm, powerful place.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...easeOut, delay: 0.46 }}
              className="mt-10 flex items-center justify-center gap-4"
            >
              {FEATURES.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2.5 rounded-full border border-white/70 bg-white/60 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm backdrop-blur"
                >
                  <Icon size={16} className="text-primary-500" />
                  {label}
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* ---- Form side ---- */}
        <div className="flex items-center justify-center px-4 py-12 sm:px-8 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...easeOut, delay: 0.15 }}
            className="w-full max-w-md"
          >
            {/* Mobile / small-screen header */}
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...easeOut, delay: 0.05 }}
              className="mb-8 flex justify-center lg:hidden"
            >
              <DevoardLogo className="max-h-16 w-auto max-w-[200px]" />
            </motion.div>

            <LoginForm onSubmit={handleLogin} loading={loading} error={error} />

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="mt-8 text-center text-sm text-gray-500"
            >
              Powered by <span className="font-semibold text-gray-700">Devoard</span>
              <span className="mx-2 text-gray-300">·</span>
              {new Date().getFullYear()}
            </motion.p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
